<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveContentTypeExportConfig"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="80%"
  >
    <v-row v-if="exportConfigItems.length == 0" justify="center" align="center">
      <v-col cols="1">
        <v-btn
          tile
          color="primary"
          prepend-icon="mdi-plus"
          outlined
          @click="addContentTypeExportConfig()"
        >
          {{ $t("Add") }}
        </v-btn>
      </v-col>
    </v-row>
    <div v-for="(item, index) in exportConfigItems" :key="item.uuid">
      <v-row>
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="error"
                icon
                @click="removeContentTypeExportConfig(item.uuid)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </v-col>
        <v-col align-self="center">
          <v-text-field
            readonly
            v-model="item.displayConditionStr"
            :label="$t('Condition')"
            :append-icon="item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
            @click:append="editCondition(item)"
          ></v-text-field>
          <v-text-field
            readonly
            v-model="item.exportMask"
            :label="$t('ExportMask')"
            :append-icon="item.exportMask == '' ? 'mdi-plus' : 'mdi-pencil'"
            @click:append="editMask(item)"
            :error-messages="maskErrors(index)"
          ></v-text-field>
        </v-col>
        <v-col align-self="center">
          <v-text-field
            :label="$t('Prefix')"
            v-model="item.exportFileNamePrefix"
          ></v-text-field>
          <v-text-field
            :label="$t('Suffix')"
            v-model="item.exportFileNameSuffix"
          ></v-text-field>
        </v-col>
        <v-col align-self="center">
          <v-text-field
            :label="$t('Extension')"
            v-model="item.exportFileNameExtension"
          ></v-text-field>
          <v-select
            :label="$t('Encoding')"
            v-model="item.exportEncoding"
            :items="encodingList"
            item-text="text"
            item-value="id"
          ></v-select>
        </v-col>
        <v-col>
          <v-switch
            v-model="item.includeOriginalFile"
            :label="$t('IncludeDocument')"
          ></v-switch>
          <v-switch
            v-model="item.exportMultipleFiles"
            :label="$t('MultipleFiles')"
          ></v-switch>
        </v-col>
        <v-col>
          <v-switch
            v-model="item.exportToServerLocation"
            :label="$t('ExportOnServer')"
          ></v-switch>
          <v-text-field
            v-if="item.exportToServerLocation"
            :label="$t('Path')"
            v-model="item.exportFilePath"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-switch
              v-model="item.exportWithHeaders"
              :label="$t('ExportWithHeaders')"
          ></v-switch>
        </v-col>
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="primary"
                icon
                @click="addContentTypeExportConfig()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Add") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <template v-slot:actions>
      <div style="position: absolute">
        <v-menu bottom right>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  text
                  small
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                  color="blue-grey"
                >
                  <v-icon large>mdi-history</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Versions") }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, i) in versionList"
              :key="i"
              @click="onVersionChange(item)"
            >
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
    <SearchCriteriaBuilderModal
      :ref="conditionModalKey"
      :contentTypeIDs="[contentType.id]"
      @on-condition-saved="onConditionChanged"
      :allowEmptyCondition="true"
    />
    <ExportConfigMaskModal
      :ref="exportMaskModalKey"
      :fieldList="contentType.fields"
      @on-mask-saved="onMaskChanged"
    />
  </base-modal>
</template>
<script>
import ExportConfigMaskModal from "@/components/administration/contentType/exportConfig/ExportConfigMaskModal.vue";
import SearchCriteriaBuilderModal from "@/components/search/SearchCriteriaBuilderModal.vue";
import ContentType from "@/ecm/models/contentType/ContentType";
import ContentTypeField from "@/ecm/models/contentType/ContentTypeField";
import ContentTypeExportConfig from "@/ecm/models/contentType/ContentTypeExportConfig";
import ContentTypeService from "@/services/ContentTypeService";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

export default {
  name: "ExportConfigModal",
  components: {
    SearchCriteriaBuilderModal,
    ExportConfigMaskModal
  },
  mounted() {
    this.getEncodings();
    this.$v.$reset();
  },
  data() {
    return {
      modal: {
        title: this.$t("ExportConfiguration"),
        key: "contentTypeExportConfigModal"
      },
      contentType: new ContentType(),
      open: false,
      conditionModalOpen: false,
      exportConfigItems: [],
      currentItem: new ContentTypeExportConfig(),
      versionList: [],
      encodingList: [],
      conditionModalKey: "conditionModalKey",
      exportMaskModalKey: "exportMaskModalKey"
    };
  },
  mixins: [validationMixin],
  validations: {
    exportConfigItems: {
      $each: {
        exportMask: { required }
      }
    }
  },
  computed: {
    modalTitle() {
      return `${this.modal.title} - ${this.contentType.name} ${this.contentType.version}`;
    },
    maskModalInstance() {
      return this.$refs[this.exportMaskModalKey];
    },
    conditionModalInstance() {
      return this.$refs[this.conditionModalKey];
    }
  },
  methods: {
    maskErrors(index) {
      const errors = [];
      if (!this.$v.exportConfigItems.$each[index].exportMask.$dirty)
        return errors;
      !this.$v.exportConfigItems.$each[index].exportMask.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    edit(dto) {
      this.contentType = new ContentType(dto);
      this.getContentTypeExportConfig();
      this.getVersions();
      this.getFields();
      this.show();
    },
    editCondition(item) {
      this.currentItem = item;
      this.conditionModalInstance.edit(item.conditionStr);
    },
    onConditionChanged(condition) {
      this.currentItem.conditionStr = condition.conditionStr;
      this.currentItem.displayConditionStr = condition.displayConditionStr;
    },
    editMask(item) {
      this.currentItem = item;
      this.maskModalInstance.edit(item.exportMask);
    },
    onMaskChanged(mask) {
      this.currentItem.exportMask = mask;
    },
    onVersionChange(version) {
      if (this.contentType.id != version.id) {
        this.contentType.id = version.id;
        this.contentType.version = version.text;
        this.getContentTypeExportConfig();
        this.getFields();
      }
    },
    getContentTypeExportConfig() {
      ContentTypeService.getExportConfig(this.contentType.id).then(data => {
        this.exportConfigItems = data.map(dto => {
          var obj = new ContentTypeExportConfig(dto);
          // obj.displayConditionStr = this.conditionModalInstance.getDisplayConditionStr(
          //   obj.conditionStr
          // );
          obj.displayConditionStr = obj.conditionStr;
          return obj;
        });
        if (this.exportConfigItems.length == 0) {
          this.addContentTypeExportConfig();
        }
      });
    },
    getVersions() {
      ContentTypeService.getVersions(this.contentType.id).then(data => {
        this.versionList = data;
      });
    },
    getFields() {
      ContentTypeService.getFields([this.contentType.id]).then(data => {
        this.contentType.fields = data.map(function(dto) {
          return new ContentTypeField(dto);
        });
      });
    },
    getEncodings() {
      ContentTypeService.getEncodingList().then(data => {
        this.encodingList = data;
      });
    },
    saveContentTypeExportConfig() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }
      var exportConfig = this.exportConfigItems.map(item => item.toDto());
      ContentTypeService.saveExportConfig(
        this.contentType.id,
        exportConfig
      ).then(data => {
        this.$emit("on-export-config-saved", this.exportConfigItems);
        this.hide();
      });
    },
    addContentTypeExportConfig() {
      this.exportConfigItems.push(new ContentTypeExportConfig());
    },
    removeContentTypeExportConfig(uuid) {
      this.exportConfigItems = this.exportConfigItems.filter(
        item => item.uuid !== uuid
      );
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    }
  }
};
</script>
